import {
    Component,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    TemplateRef,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog"; // Import MatDialogRef for dialog reference
import { BaseChartLogic } from "app/analytics/BaseChartLogic";
import {
    AnalyticsService,
    AppUsageChartData,
    AppUsageChartDataPoint,
    AppUsageSession,
    AuthorizationService,
    ExportService,
    ExportType,
    LoggingService,
    ManualOpenDataPoint,
    ToastrService,
} from "app/shared";
import { AppUsageConfig } from "app/shared/model/charts/user-analytic-configurations.model";
import { FormatTimePipe } from "app/shared/pipe/fortmatTime.pipe";

interface SessionExpandedState {
    [sessionId: string]: boolean;
}

interface ExpandedState {
    [userId: number]: {
        expanded: boolean;
        sessions: SessionExpandedState;
    };
}

interface ChartExpandedState {
    [permissionType: string]: {
        expanded: boolean;
        users: ExpandedState;
    };
}

@Component({
    selector: "app-usage-base-grid",
    templateUrl: "./app-usage-base-grid.component.html",
    styleUrls: ["./app-usage-base-grid.component.css"],
    standalone: false,
})
export class AppUsageBaseGridComponent
    extends BaseChartLogic<AppUsageConfig, AppUsageChartData>
    implements OnInit, OnDestroy {
    @Input() startDate: Date | null = null;
    @Input() endDate: Date | null = null;
    @Input() hasData: boolean = true;
    @Input() showTitle: boolean = true;

    protected listData: AppUsageChartDataPoint[] = [];
    protected isLoading: boolean = false;

    expandedState: ChartExpandedState = {};
    manualVisibilityMap = new Map<string, { [section: string]: boolean }>();

    // Modal management with MatDialog
    modalData: AppUsageSession | null = null;
    modalTitle: string = "";

    // Dialog Reference for the modal
    dialogRef: MatDialogRef<any> | null = null;

    // Modal template reference
    @ViewChild("modalTemplate") modalTemplate!: TemplateRef<any>;

    constructor(
        _analyticsService: AnalyticsService,
        _authService: AuthorizationService,
        _exportService: ExportService,
        _toastrService: ToastrService,
        _loggingService: LoggingService,
        _ngZone: NgZone,
        private dialog: MatDialog // Injecting MatDialog for modal functionality
    ) {
        super(
            _analyticsService,
            _authService,
            _exportService,
            _toastrService,
            _loggingService,
            AppUsageConfig,
            _ngZone
        );

        this.handleDataSubscription(this.analyticsService.appUsageDataChange);

        this.analyticsService.appUsageDataChange.subscribe((data) => {
            this.listData = data.DataPoints.map((item) =>
                Object.assign(new AppUsageChartDataPoint(), item)
            );

            this.expandedState = {};
            this.listData.forEach((dataPoint) => {
                this.expandedState[dataPoint.TypeOfUser] = {
                    expanded: false,
                    users: {},
                };
            });

            this.hasData = true;

            this.isLoading = false;
        });
    }

    ngOnInit(): void {
        super.init();

        this.exportService.appUsageExportOptions(
            this.updateChartTitle() || "App Usage Report",
            [
                ExportType.PDF,
                ExportType.Excel
            ]
        );
    }

    ngOnDestroy(): void {
        super.cleanup();
    }

    userSearch: { [permissionType: string]: string } = {};

    filterUsers(permissionType: string, users: any[]): any[] {
        const searchTerm = this.userSearch[permissionType]?.toLowerCase() || "";
        return users.filter((user) =>
            `${user.FirstName} ${user.LastName} ${user.UserName}`
                .toLowerCase()
                .includes(searchTerm)
        );
    }

    dataPointKey: string = "Total";

    private userTypeMap: { [key: number]: string } = {
        0: "All",
        1: "Application Administrator",
        2: "Komatsu Employee",
        3: "Parent Company",
        4: "Minesite",
        5: "Modified Distributor/Customer"
    };

    private convertMsToDuration(ms: number): string {
        if (!ms || ms <= 0) return "0 min";
        const totalMinutes = ms / 60000;
        if (totalMinutes >= 60) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = Math.floor(totalMinutes % 60);
            return `${hours} hr ${minutes} min`;
        }
        return `${totalMinutes.toFixed(2)} min`;
    }

    private buildDateRangeString(): string {

        if (this.filterSettings?.FilterByStartDate && this.filterSettings?.FilterByEndDate) {

            return `${new Date(this.filterSettings.FilterByStartDate).toLocaleString()} - ${new Date(this.filterSettings.FilterByEndDate).toLocaleString()}`;

        }

        // fallback to Period label
        if (this.filterSettings?.Period) {

            const periodMap: { [key: string]: string } = {
                "1H": "Last 1 Hour",
                "2H": "Last 2 Hours",
                "24H": "Last 24 Hours",
                "48H": "Last 48 Hours",
                "72H": "Last 72 Hours",
                "1D": "Last 1 Day",
                "1W": "Last 1 Week",
                "1M": "Last 1 Month",
                "3M": "Last 3 Months",
                "6M": "Last 6 Months",
                "1Y": "Last 1 Year",
                "ALL": "All Time"
            };

            return periodMap[this.filterSettings.Period] || this.filterSettings.Period;

        }

        return "All Time";
    }

    getDataPointDisplayValue(dataPoint: AppUsageChartDataPoint): any {
        return dataPoint.TypeOfUser;
    }

    buildFilterSettings(): void {
        this.addPeriodtoFilterSettings();
        this.addPermissionsToFilterSettings();
    }

    refreshData(): void {
        this.hasData = false;
        this.isLoading = true;
        this.addPeriodtoFilterSettings();
        this.addPermissionsToFilterSettings();
        this.analyticsService.getAppUsageData(this.filterSettings);
    }

    toggleDataPointExpand(permissionType: string): void {
        // Collapse all other panels
        for (const key in this.expandedState) {
            if (this.expandedState.hasOwnProperty(key)) {
                this.expandedState[key].expanded = false;
            }
        }

        // Toggle the selected one
        const currentState = this.expandedState[permissionType]?.expanded || false;
        this.expandedState[permissionType] = {
            expanded: !currentState,
            users: this.expandedState[permissionType]?.users || {},
        };
    }

    isDataPointExpanded(permissionType: string): boolean {
        return this.expandedState[permissionType]?.expanded || false;
    }

    toggleUserExpand(permissionType: string, userId: number): void {
        if (!this.expandedState[permissionType]) {
            this.expandedState[permissionType] = { expanded: false, users: {} };
        }

        const userState = this.expandedState[permissionType].users[userId] || {
            expanded: false,
            sessions: {},
        };

        userState.expanded = !userState.expanded;
        this.expandedState[permissionType].users[userId] = userState;
    }

    isUserExpanded(permissionType: string, userId: number): boolean {
        return (
            this.expandedState[permissionType]?.users?.[userId]?.expanded || false
        );
    }

    toggleSessionExpand(
        permissionType: string,
        userId: number,
        sessionId: string
    ): void {
        const sessionStates =
            this.expandedState[permissionType]?.users?.[userId]?.sessions || {};
        sessionStates[sessionId] = !sessionStates[sessionId];

        const users = this.expandedState[permissionType]?.users || {};
        if (!users[userId]) {
            users[userId] = { expanded: true, sessions: {} };
        }
        users[userId].sessions = sessionStates;
        this.expandedState[permissionType].users = users;
    }

    isSessionExpanded(
        permissionType: string,
        userId: number,
        sessionId: string
    ): boolean {
        return (
            this.expandedState[permissionType]?.users?.[userId]?.sessions?.[
            sessionId
            ] || false
        );
    }

    toggleVisibility(section: string, manual: ManualOpenDataPoint): void {
        const id = JSON.stringify(manual);
        if (!this.manualVisibilityMap.has(id)) {
            this.manualVisibilityMap.set(id, {});
        }
        const state = this.manualVisibilityMap.get(id)!;
        state[section] = !state[section];
    }

    isVisible(section: string, manual: ManualOpenDataPoint): boolean {
        const id = JSON.stringify(manual);
        return this.manualVisibilityMap.get(id)?.[section] || false;
    }

    getPagesViewed(pagesViewed): any[] {
        return Object.keys(pagesViewed).map((key) => ({
            pageNumber: key,
            count: pagesViewed[key],
        }));
    }

    getSearchTermsUsed(searchTermsUsed): any[] {
        return Object.keys(searchTermsUsed).map((key) => ({
            term: key,
            count: searchTermsUsed[key],
        }));
    }

    hasSearchTerms(manual: any): boolean {
        return Object.keys(manual.SearchTermsUsed || {}).length > 0;
    }

    hasPagesViewed(manual: any): boolean {
        return Object.keys(manual.PagesViewed || {}).length > 0;
    }

    // Open modal with session details
    openModal(session: AppUsageSession): void {
        this.modalData = session;
        this.modalTitle = "Session Details";

        // Open the dialog using MatDialog and passing modal data
        setTimeout(() => {
            this.dialogRef = this.dialog.open(this.modalTemplate, {
                width: "60%",
            });
        });
    }

    // Close modal
    closeModal(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    private buildExcelExportData(): {
        summary: any[];
        users: any[];
        sessions: any[];
        details: any[];
    } {
        const summaryArray: any[] = [];
        const usersArray: any[] = [];
        const sessionsArray: any[] = [];
        const detailsArray: any[] = [];

        this.listData.forEach((dataPoint) => {

            const permissionType = this.userTypeMap[dataPoint.TypeOfUser] ?? "Unknown";
            const filteredUsers = this.filterUsers(
                permissionType,
                dataPoint.UsageData || []
            );

            // Build Summary Sheet
            const totalSessions = filteredUsers.reduce(
                (sum, user) => sum + (user.AppUsageSessions?.length || 0),
                0
            );

            const totalSessionTime = filteredUsers.reduce(
                (sum, user) =>
                    sum +
                    (user.AppUsageSessions?.reduce(
                        (sessionSum, session) =>
                            sessionSum + (session.TotalSessionTime || 0),
                        0
                    ) || 0),
                0
            );

            const avgSessionTime =
                totalSessions > 0 ? totalSessionTime / totalSessions : 0;

            summaryArray.push({
                PermissionType: permissionType,
                UserCount: filteredUsers.length,
                TotalSessions: totalSessions,
                TotalSessionTime: this.convertMsToDuration(totalSessionTime),
                AvgSessionTime: this.convertMsToDuration(avgSessionTime)
            });

            // Processs users
            filteredUsers.forEach((user) => {

                const userSessions: AppUsageSession[] =
                    user.AppUsageSessions || [];

                // Calculate user totals 
                const totalUserSessionTime =
                    userSessions.reduce(
                        (sum, session) => sum + (session.TotalSessionTime || 0),
                        0
                    );

                const avgUserSessionTime =
                    userSessions.length > 0
                        ? totalUserSessionTime / userSessions.length
                        : 0;

                // USERS SHEET
                usersArray.push({
                    PermissionType: permissionType,
                    UserName: `${user.FirstName} ${user.LastName}`,
                    Email: user.UserName,
                    TotalSessions: userSessions.length,
                    TotalSessionTime: this.convertMsToDuration(totalUserSessionTime),
                    AvgSessionTime: this.convertMsToDuration(avgUserSessionTime)
                });

                // SESSIONS + DETAILS
                userSessions.forEach((session) => {

                    const duration = this.convertMsToDuration(session.TotalSessionTime || 0);

                    sessionsArray.push({
                        PermissionType: permissionType,
                        UserName: `${user.FirstName} ${user.LastName}`,
                        Email: user.UserName,
                        SessionId: session.SessionId,
                        LoginTime: session.LoggedIn,
                        Duration: duration,
                        ManualsCount: session.ManualsOpened?.length || 0
                    });

                    // DETAILS SHEET
                    if (session.ManualsOpened?.length) {

                        session.ManualsOpened.forEach((manual) => {

                            const pagesViewed = this.getPagesViewed(
                                manual.PagesViewed || {}
                            );

                            const searchTermsUsed = this.getSearchTermsUsed(
                                manual.SearchTermsUsed || {}
                            );

                            detailsArray.push({
                                PermissionType: permissionType,
                                UserName: `${user.FirstName} ${user.LastName}`,
                                Email: user.UserName,
                                SessionId: session.SessionId,
                                Duration: duration,
                                ManualName:
                                    manual.Document?.FileName
                                    || "Unnamed Document",
                                PagesViewed:
                                    pagesViewed
                                        .map(p => `${p.pageNumber} (${p.count})`)
                                        .join(", "),
                                SearchTermsUsed:
                                    searchTermsUsed
                                        .map(s => `${s.term} (${s.count})`)
                                        .join(", ")
                            });
                        });
                    }
                });
            });
        });

        return {
            summary: summaryArray,
            users: usersArray,
            sessions: sessionsArray,
            details: detailsArray
        };
    }

    private buildPdfExportData(): {
        title: string;
        dateRange: string;
        generatedOn: string;
        summary: any[];
        permissionGroups: any[];
    } {

        const summary: any[] = [];
        const permissionGroups: any[] = [];

        const safeListData = Array.isArray(this.listData) ? this.listData : [];

        safeListData.forEach(dp => {

            const permissionType =
                dp && dp.TypeOfUser !== undefined && dp.TypeOfUser !== null
                    ? this.userTypeMap[dp.TypeOfUser] ?? "Unknown"
                    : "Unknown";

            const users = Array.isArray(dp?.UsageData) ? dp.UsageData : [];

            const filteredUsers = Array.isArray(users)
                ? this.filterUsers(permissionType, users) || []
                : [];

            // calculate totals for summary (USE MILLISECONDS FIRST)
            const totalSessions = filteredUsers.reduce(
                (sum, user) => sum + (user.AppUsageSessions?.length || 0),
                0
            );

            const totalSessionTimeMs = filteredUsers.reduce(
                (sum, user) =>
                    sum +
                    (user.AppUsageSessions?.reduce(
                        (sessionSum, session) =>
                            sessionSum + (session.TotalSessionTime || 0),
                        0
                    ) || 0),
                0
            );

            const avgSessionTimeMs =
                totalSessions > 0 ? totalSessionTimeMs / totalSessions : 0;

            summary.push({
                PermissionType: permissionType,
                UserCount: filteredUsers.length || 0,
                TotalSessions: totalSessions,
                TotalSessionTime: this.convertMsToDuration(totalSessionTimeMs),
                AvgSessionTime: this.convertMsToDuration(avgSessionTimeMs)
            });

            const usersForGroup = filteredUsers.map(user => {

                if (!user) {
                    return {
                        name: "Unknown User",
                        email: "",
                        totalSessions: 0,
                        totalTime: "0 min",
                        sessions: []
                    };
                }

                const safeSessions = Array.isArray(user.AppUsageSessions)
                    ? user.AppUsageSessions
                    : [];

                // FIX: calculate total time in ms FIRST, then convert
                const totalTimeMs = safeSessions.reduce(
                    (sum, session) => sum + (session.TotalSessionTime || 0),
                    0
                );

                const sessions = safeSessions.map(session => {

                    if (!session) {
                        return {
                            sessionId: "",
                            loginTime: "",
                            duration: "0 min",
                            manualsCount: 0,
                            manuals: []
                        };
                    }

                    const safeManuals = Array.isArray(session.ManualsOpened)
                        ? session.ManualsOpened
                        : [];

                    const manuals = safeManuals.map(manual => {

                        if (!manual) {
                            return {
                                manualName: "Unnamed Document",
                                pagesViewed: "",
                                searchTerms: ""
                            };
                        }

                        const documentName =
                            manual.Document?.FileName ??
                            manual.Document?.Name ??
                            "Unnamed Document";

                        const pagesViewed =
                            manual.PagesViewed && typeof manual.PagesViewed === "object"
                                ? Object.entries(manual.PagesViewed)
                                    .map(([p, c]) => `${p} (${c})`)
                                    .join(", ")
                                : "";

                        const searchTerms =
                            manual.SearchTermsUsed && typeof manual.SearchTermsUsed === "object"
                                ? Object.entries(manual.SearchTermsUsed)
                                    .map(([t, c]) => `${t} (${c})`)
                                    .join(", ")
                                : "";

                        return {
                            manualName: documentName,
                            pagesViewed,
                            searchTerms
                        };
                    });

                    return {
                        sessionId: session.SessionId ?? "",
                        loginTime: session.LoggedIn ?? "",
                        duration: this.convertMsToDuration(session.TotalSessionTime || 0),
                        manualsCount: safeManuals.length,
                        manuals: manuals.length ? manuals : [{
                            manualName: "No manuals",
                            pagesViewed: "",
                            searchTerms: ""
                        }]
                    };
                });

                return {
                    name: `${user.FirstName ?? ""} ${user.LastName ?? ""}`.trim() || "Unknown User",
                    email: user.UserName ?? "",
                    totalSessions: safeSessions.length,
                    totalTime: this.convertMsToDuration(totalTimeMs),
                    sessions: sessions.length ? sessions : [{
                        sessionId: "",
                        loginTime: "",
                        duration: "0 min",
                        manualsCount: 0,
                        manuals: [{
                            manualName: "No sessions",
                            pagesViewed: "",
                            searchTerms: ""
                        }]
                    }]
                };
            });

            permissionGroups.push({
                permissionType,
                users: usersForGroup.length ? usersForGroup : [{
                    name: "No users available",
                    email: "",
                    totalSessions: 0,
                    totalTime: "0 min",
                    sessions: [{
                        sessionId: "",
                        loginTime: "",
                        duration: "0 min",
                        manualsCount: 0,
                        manuals: [{
                            manualName: "",
                            pagesViewed: "",
                            searchTerms: ""
                        }]
                    }]
                }]
            });

        });

        return {
            title: this.updateChartTitle?.() ?? "App Usage Report",

            dateRange: this.buildDateRangeString(),

            generatedOn: new Date().toLocaleString(),

            summary: Array.isArray(summary) ? summary : [],

            permissionGroups: Array.isArray(permissionGroups)
                ? permissionGroups
                : []
        };
    }

    protected override handleExport(
        type: ExportType,
        isExportingAll: Boolean = false
    ): void {
        switch (type) {

            case ExportType.Excel: {

                const excelExportData = {
                    ...this.buildExcelExportData(),
                    dateRange: this.buildDateRangeString()
                };

                this.exportService.exportAppUsageDataToExcelMultiSheet(
                    excelExportData,
                    this.updateChartTitle() || "App Usage Report"
                );
                return;
            }

            case ExportType.PDF: {

                const excelPdfData = this.buildPdfExportData();
                this.exportService.exportAppUsageToPDF(
                    excelPdfData,
                    this.updateChartTitle() || "App Usage Report"
                );
                return;
            }

            default: {
                super.handleExport(type, isExportingAll);
                return;
            }
        }
    }
}
